import React, { useState } from 'react';
import { func, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';

import TermsConditions from '../../components/Terms/TermsAndConditions';

import { manageReturnUrl } from '../../helpers/session';
import { enqueueSnackbar } from '../../actions/applicationMessageActions';
import { fullYear } from '../../utils/date-util';

import classes from './AuthCore.module.css';

const ValidationSchema = Yup.object().shape({
    email: Yup.string()
        .email('Must be an email address')
        .required('Email address required'),
    password: Yup.string()
        .required('Password required')
});

const Copyright = () => {
    return (
        <React.Fragment>
            <div className={classes.copyright}>
                Copyright &copy; {fullYear()} WennSoft, Inc. All rights reserved.
            </div>
        </React.Fragment>
    );
};

const SignIn = props => {
    const formik = useFormik({
        initialValues: {
            email:    '',
            password: ''
        },
        validationSchema: ValidationSchema,
        onSubmit:         async (values, { setSubmitting }) => {
            setSubmitting(true);

            const lowerCaseEmail = values.email.toLowerCase();

            const authState = await props.signIn(
                lowerCaseEmail,
                values.password
            );

            if (authState.errorMessage) {
                props.enqueueSnackbar(authState.errorMessage, 'error');
                setSubmitting(false);
                values.password = '';
            }
        }
    });

    const [tocModalOpen, setTocModalOpen] = useState(false);

    const handleSignUp = event => {
        event.preventDefault();

        props.history.push('/signUp');
    };

    const handleForgotPassword = event => {
        event.preventDefault();

        props.history.push('/forgotpassword');
    };

    manageReturnUrl(props.location, props.history);

    return (
        <div className={classes.AuthCore}>
            <Paper className={classes.AuthCoreContainer}>
                <div className={classes.AuthCoreHeroContainer}>
                    <Copyright />
                </div>
                <div className={classes.AuthCoreFormContainer}>
                    <div className={classes.AuthCoreSignInHeader}>
                        <div className={classes.boblogo}><img alt="BOB" src="/assets/images/BOB-BLACK.png" style={{width: '300px', height: '158px'}} /></div>
                        <div className={classes.boblogo}>
                            <span className={classes.subtitle}>
                                The only building analytics tool to schedule a fix
                            </span>
                        </div>
                    </div>
                    <form
                        onSubmit={formik.handleSubmit}
                        className={classes.form}
                    >
                        <FormControl className={classes.formControl}>
                            <InputLabel
                                htmlFor="email"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.email) && Boolean(formik.touched.email)}
                            >
                                Email
                            </InputLabel>
                            <Input
                                id="email"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                classes={{
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }}
                                error={Boolean(formik.errors.email) && Boolean(formik.touched.email)}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                            />
                            {formik.touched.email &&
                             formik.errors.email && <FormHelperText className={classes.error}>{formik.errors.email}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.formControl}>
                            <InputLabel
                                htmlFor="password"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.password) && Boolean(formik.touched.password)}
                            >
                                Password
                            </InputLabel>
                            <Input
                                id="password"
                                name="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                type="password"
                                classes={{
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }}
                                error={Boolean(formik.errors.password) && Boolean(formik.touched.password)}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                            />
                            {formik.touched.password &&
                             formik.errors.password && <FormHelperText className={classes.error}>{formik.errors.password}</FormHelperText>}
                        </FormControl>
                        <div className={classes.buttonContainer}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                className={classes.secondary}
                                onClick={handleSignUp}
                            >
                                Sign Up
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={formik.isSubmitting}
                                className={classes.primary}
                            >
                                Sign In
                            </Button>
                        </div>
                    </form>
                    <div className={classes.termsAndConditionsContainer}>
                        <button
                            onClick={handleForgotPassword}
                            className={classes.tocButton}
                        >
                            Forgot Password?
                        </button>
                        <span>|</span>
                        <button
                            className={classes.tocButton}
                            onClick={() => setTocModalOpen(true)}
                        >
                            Terms and Conditions
                        </button>
                        <TermsConditions open={tocModalOpen} handleClose={() => setTocModalOpen(false)} />
                    </div>
                </div>
            </Paper>
        </div>
    );
};

SignIn.defaultProps = {
    enqueueSnackbar: () => {},
    history:         {
        replace: () => {}
    },
    location: {
        state: {}
    },
    signIn: () => {}
};

SignIn.propTypes = {
    enqueueSnackbar: func,
    history:         shape({
        replace: func
    }),
    location: shape({
        state: shape({
            from: string
        })
    }),
    signIn: func
};

const mapDispatchToProps = {
    enqueueSnackbar
};

export default withRouter(connect(null, mapDispatchToProps)(SignIn));
